import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './AuthInterface.css';

const AuthInterface = () => {
  const { 
    isAuthenticated,
    sessionId,
    roomName,
    userIdentity,
    appId,
    expiresIn,
    loading, 
    error, 
    login, 
    connectToVoice, 
    disconnectFromVoice,
    participants,
    connectionState
  } = useAuth();

  // Generate truly unique user identity for each session
  const generateUniqueUserId = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 10000);
    const sessionId = Math.random().toString(36).substring(2, 8);
    return `user_${timestamp}_${randomNum}_${sessionId}`;
  };

  const [formData, setFormData] = useState(() => ({
    userIdentity: generateUniqueUserId(),
    appId: '68b87f015ec178bf7db86e52',
    apiKey: "f59a072e-550e-4530-b470-ce4c59e167a3",
    language: 'en-US'
  }));

  // Function to refresh user identity (for new session)
  const refreshUserIdentity = () => {
    setFormData(prev => ({
      ...prev,
      userIdentity: generateUniqueUserId()
    }));
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await login(formData.userIdentity, formData.appId, formData.apiKey, formData.language);
  };

  const handleConnect = async () => {
    await connectToVoice();
  };

  const handleDisconnect = async () => {
    await disconnectFromVoice();
  };

  return (
    <div className="auth-interface">
      <div className="auth-container">
        <div className="auth-header">
          <h2>🔐 Voice Assistant Authentication</h2>
        </div>

        {/* Login Form */}
        {!isAuthenticated && (
          <div className="login-section">
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="userIdentity">User Identity:</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    id="userIdentity"
                    name="userIdentity"
                    value={formData.userIdentity}
                    onChange={handleInputChange}
                    placeholder="Enter user identity"
                    required
                    readOnly
                    title="Unique identifier for this session"
                  />
                  <button
                    type="button"
                    onClick={refreshUserIdentity}
                    className="btn-refresh"
                    title="Generate new session ID"
                  >
                    🔄
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="appId">App ID:</label>
                <input
                  type="text"
                  id="appId"
                  name="appId"
                  value={formData.appId}
                  onChange={handleInputChange}
                  placeholder="Enter app ID"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="apiKey">API Key:</label>
                <input
                  type="text"
                  id="apiKey"
                  name="apiKey"
                  value={formData.apiKey}
                  onChange={handleInputChange}
                  placeholder="Enter API key"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="language">Language:</label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  required
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish (Spain)</option>
                  <option value="es-MX">Spanish (Mexico)</option>
                  <option value="fr-FR">French</option>
                  <option value="de-DE">German</option>
                  <option value="it-IT">Italian</option>
                  <option value="pt-BR">Portuguese (Brazil)</option>
                  <option value="zh-CN">Chinese (Simplified)</option>
                  <option value="zh-TW">Chinese (Traditional)</option>
                  <option value="ja-JP">Japanese</option>
                  <option value="ko-KR">Korean</option>
                  <option value="ru-RU">Russian</option>
                  <option value="ar-SA">Arabic</option>
                  <option value="hi-IN">Hindi</option>
                  <option value="th-TH">Thai</option>
                  <option value="vi-VN">Vietnamese</option>
                  <option value="nl-NL">Dutch</option>
                  <option value="sv-SE">Swedish</option>
                  <option value="da-DK">Danish</option>
                  <option value="no-NO">Norwegian</option>
                  <option value="fi-FI">Finnish</option>
                  <option value="pl-PL">Polish</option>
                  <option value="tr-TR">Turkish</option>
                  <option value="he-IL">Hebrew</option>
                </select>
              </div>

              <button
                type="submit"
                className="btn-login"
                disabled={loading}
              >
                {loading ? '🔄 Logging in...' : '🔐 Login'}
              </button>
            </form>

            {error && (
              <div className="error-message">
                ❌ {error}
              </div>
            )}
          </div>
        )}

        {/* Connection Status */}
        {isAuthenticated && (
          <div className="connection-section">
            <div className="auth-info">
              <h3>✅ Authenticated</h3>
              <div className="info-grid">
                <div className="info-item">
                  <strong>User:</strong> {userIdentity}
                </div>
                <div className="info-item">
                  <strong>App ID:</strong> {appId}
                </div>
                <div className="info-item">
                  <strong>Room:</strong> {roomName}
                </div>
                <div className="info-item">
                  <strong>Session:</strong> {sessionId}
                </div>
                <div className="info-item">
                  <strong>Expires:</strong> {expiresIn}s
                </div>
              </div>
            </div>

            <div className="voice-controls">
              <div className="connection-status">
                <span className={`status-dot ${connectionState}`}></span>
                <span className="status-text">
                  Voice: {connectionState}
                </span>
              </div>

              <div className="control-buttons">
                {connectionState === 'disconnected' && (
                  <button 
                    onClick={handleConnect}
                    className="btn-connect"
                    disabled={loading}
                  >
                    🎤 Connect to Voice
                  </button>
                )}

                {connectionState === 'connected' && (
                  <button 
                    onClick={handleDisconnect}
                    className="btn-disconnect"
                  >
                    🔌 Disconnect
                  </button>
                )}
              </div>
            </div>

            {/* Participants */}
            {participants.length > 0 && (
              <div className="participants-section">
                <h4>👥 Participants ({participants.length})</h4>
                <div className="participants-list">
                  {participants.map((participant, index) => (
                    <div key={index} className="participant-item">
                      <span className="participant-icon">👤</span>
                      <span className="participant-name">{participant.identity}</span>
                      <span className="participant-status">
                        {participant.isSpeaking ? '🎤 Speaking' : '🔇 Silent'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthInterface; 